type RunWhenIdleOptions = {
  /**
   * Upper bound (ms) on how long to wait for an idle window before running the
   * task anyway. Backstops `requestIdleCallback` so a perpetually-busy main
   * thread can't starve the task indefinitely.
   */
  timeout?: number;
};

/**
 * Run `task` once the browser is idle (after paint), so expensive work doesn't
 * block the current render or compete with user interactions. Uses
 * `requestIdleCallback` where available (it yields to input), falling back to a
 * short `setTimeout` otherwise. No-op during SSR.
 *
 * Returns a canceler; call it (e.g. from a React effect cleanup) to unschedule
 * the task if it hasn't run yet.
 */
export function runWhenIdle(
  task: () => void,
  { timeout = 2000 }: RunWhenIdleOptions = {},
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(task, { timeout });

    return () => {
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(handle);
      }
    };
  }

  const handle = window.setTimeout(task, 200);
  return () => window.clearTimeout(handle);
}

/**
 * How long after the window `load` event startup work is still presumed to be
 * in flight. On funder pages `load` fires while round data is still streaming
 * in and the video player is still mounting (~271ms into a traced Overview
 * load, with the critical fetch/mount work running to ~670ms), so "after
 * load" is not "startup is done". Sized with headroom for slower devices and
 * networks than the traced ones.
 */
export const STARTUP_GRACE_MS = 3000;

type RunWhenStartupSettledOptions = {
  /** Overrides `STARTUP_GRACE_MS` (tests). */
  graceMs?: number;
  /** Idle backstop forwarded to `runWhenIdle` for the final stage. */
  timeout?: number;
};

/**
 * Run `task` once startup has actually settled: after the window `load`
 * event, then a fixed grace period, then an idle window. Use this instead of
 * bare `runWhenIdle` for deferred *startup* work (background prefetches,
 * analytics bring-up): `requestIdleCallback`'s timeout is "no later than",
 * not "wait this long", and right after `load` the main thread has genuine
 * idle gaps between the fetches and mounts the user is actually waiting on —
 * so an idle callback scheduled at load lands its work in the middle of the
 * critical startup window.
 *
 * The grace period is a fixed delay rather than a readiness signal (video
 * `canplay`, queries settling) on purpose: pages without video or queries
 * would never settle, and the delay needs no coupling between this scheduler
 * and product surfaces. Background work merely starts a beat later than the
 * theoretical optimum on fast loads.
 *
 * Returns a canceler; call it to unschedule whichever stage is pending.
 */
export function runWhenStartupSettled(
  task: () => void,
  { graceMs = STARTUP_GRACE_MS, timeout }: RunWhenStartupSettledOptions = {},
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  let cancel: () => void;

  const afterLoad = () => {
    const handle = window.setTimeout(() => {
      cancel = runWhenIdle(task, { timeout });
    }, graceMs);
    cancel = () => window.clearTimeout(handle);
  };

  if (document.readyState === "complete") {
    afterLoad();
  } else {
    window.addEventListener("load", afterLoad, { once: true });
    cancel = () => window.removeEventListener("load", afterLoad);
  }

  return () => cancel();
}

/**
 * Await a break in main-thread work: resolves in a new task, so pending input
 * and paint can run between slices of a long computation. Prefers
 * `scheduler.yield()` where available (its continuation is prioritized ahead
 * of other queued tasks), falling back to a 0ms timeout.
 *
 * Caveat for latency-sensitive callers: in a background tab the timeout
 * fallback is throttled (>= 1s per yield in Safari/Firefox), so sliced work
 * there can stretch out over seconds. Fine for idempotent background
 * pipelines; don't slice anything the user is waiting on with this.
 */
export function yieldToMain(): Promise<void> {
  const scheduler = (
    globalThis as { scheduler?: { yield?: () => Promise<void> } }
  ).scheduler;

  if (typeof scheduler?.yield === "function") {
    return scheduler.yield();
  }

  return new Promise((resolve) => setTimeout(resolve, 0));
}

type RunSlicedOptions = {
  /**
   * How long (ms) a slice may run before yielding. The default of 8ms is half
   * a 60Hz frame: even if a slice starts right before the next paint deadline
   * and overshoots its budget on the final step, input handling and paint
   * still fit in the same frame.
   */
  budgetMs?: number;
  /** Aborting stops the run at the next slice boundary. */
  signal?: AbortSignal;
};

const DEFAULT_SLICE_BUDGET_MS = 8;

/**
 * Run a sequence of synchronous steps cooperatively: steps execute in order,
 * and whenever the running slice exceeds its time budget the sequence yields
 * to the main thread (via `yieldToMain`) so input and paint stay responsive.
 * Use this for divisible background work (extraction, batch transforms) that
 * would otherwise run long enough to stall interactions — one indivisible
 * step should stay well under ~50ms or the slicing can't help.
 *
 * Returns the step results in order, or `null` if `signal` aborted first —
 * a `null` means the caller must discard the pass (results from steps that
 * already ran are dropped; no partial output escapes). Steps themselves are
 * never interrupted mid-run; abort is only observed at slice boundaries.
 */
export async function runSliced<T>(
  steps: Iterable<() => T>,
  { budgetMs = DEFAULT_SLICE_BUDGET_MS, signal }: RunSlicedOptions = {},
): Promise<T[] | null> {
  if (signal?.aborted) {
    return null;
  }

  const results: T[] = [];
  let sliceStartedAt = performance.now();

  for (const step of steps) {
    results.push(step());

    if (performance.now() - sliceStartedAt >= budgetMs) {
      await yieldToMain();

      // Only an await can observe an abort (cancellation runs on this same
      // thread), so checking right after resuming covers every case.
      if (signal?.aborted) {
        return null;
      }

      sliceStartedAt = performance.now();
    }
  }

  return results;
}
